/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListTriggerDefinitionsRequest,
  ListTriggerDefinitionsSecurity,
} from "../models/operations/listtriggerdefinitions.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildTriggerDefinitionsQuery,
  prefetchTriggerDefinitions,
  queryKeyTriggerDefinitions,
  TriggerDefinitionsQueryData,
} from "./triggerDefinitions.core.js";
export {
  buildTriggerDefinitionsQuery,
  prefetchTriggerDefinitions,
  queryKeyTriggerDefinitions,
  type TriggerDefinitionsQueryData,
};

export type TriggerDefinitionsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listTriggerDefinitions triggers
 *
 * @remarks
 * List static trigger definitions available to a project.
 */
export function useTriggerDefinitions(
  request?: ListTriggerDefinitionsRequest | undefined,
  security?: ListTriggerDefinitionsSecurity | undefined,
  options?: QueryHookOptions<
    TriggerDefinitionsQueryData,
    TriggerDefinitionsQueryError
  >,
): UseQueryResult<TriggerDefinitionsQueryData, TriggerDefinitionsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildTriggerDefinitionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listTriggerDefinitions triggers
 *
 * @remarks
 * List static trigger definitions available to a project.
 */
export function useTriggerDefinitionsSuspense(
  request?: ListTriggerDefinitionsRequest | undefined,
  security?: ListTriggerDefinitionsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    TriggerDefinitionsQueryData,
    TriggerDefinitionsQueryError
  >,
): UseSuspenseQueryResult<
  TriggerDefinitionsQueryData,
  TriggerDefinitionsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildTriggerDefinitionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setTriggerDefinitionsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: TriggerDefinitionsQueryData,
): TriggerDefinitionsQueryData | undefined {
  const key = queryKeyTriggerDefinitions(...queryKeyBase);

  return client.setQueryData<TriggerDefinitionsQueryData>(key, data);
}

export function invalidateTriggerDefinitions(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "triggers", "listDefinitions", ...queryKeyBase],
  });
}

export function invalidateAllTriggerDefinitions(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "triggers", "listDefinitions"],
  });
}
